import { useState, useMemo, useCallback, memo } from 'react'
import {
  Paper,
  Box,
  Typography,
  Chip,
  Button,
  Divider,
  TextField,
  Collapse,
  CircularProgress,
  IconButton,
} from '@mui/material'
import SyncIcon from '@mui/icons-material/Sync'
import PeopleIcon from '@mui/icons-material/People'
import AddIcon from '@mui/icons-material/Add'
import ExpandMoreIcon from '@mui/icons-material/ExpandMore'
import ExpandLessIcon from '@mui/icons-material/ExpandLess'
import toast from 'react-hot-toast'
import {
  useScheduleProductionRequirements,
  useUpdateProductionRequirements,
} from './Scheduler.data'
import { SynchronizedLinesRowType } from './Scheduler.types'

// --- Period parsing ---

type PeriodWindow = { start: number; end: number }

const parsePeriods = (periods: string | undefined | null): PeriodWindow[] => {
  if (!periods || periods.trim() === '') return []
  const matches = periods.matchAll(/\((\d+),\s*(\d+)\)/g)
  const result: PeriodWindow[] = []
  for (const m of matches) {
    result.push({ start: parseInt(m[1], 10), end: parseInt(m[2], 10) })
  }
  return result
}

const DAY_LABELS = ['Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat', 'Sun']
const TOTAL_HOURS = 168

// --- PeriodGantt component ---

type PeriodGanttProps = { periods: string }

const PeriodGantt = memo(({ periods }: PeriodGanttProps) => {
  const windows = useMemo(() => parsePeriods(periods), [periods])

  const hourSet = useMemo(() => {
    const s = new Set<number>()
    for (const w of windows) {
      for (let h = w.start; h < w.end && h < TOTAL_HOURS; h++) {
        s.add(h)
      }
    }
    return s
  }, [windows])

  if (windows.length === 0) {
    return (
      <Typography
        variant="body2"
        sx={{ fontSize: '0.75rem', color: 'text.secondary' }}
      >
        No period windows defined
      </Typography>
    )
  }

  return (
    <Box sx={{ width: '100%', userSelect: 'none' }}>
      {/* Day labels */}
      <Box sx={{ display: 'flex', width: '100%', mb: '2px' }}>
        {DAY_LABELS.map((day) => (
          <Box
            key={day}
            sx={{
              flex: 1,
              textAlign: 'center',
              fontSize: '0.65rem',
              fontWeight: 600,
              color: 'text.secondary',
              letterSpacing: '0.03em',
            }}
          >
            {day}
          </Box>
        ))}
      </Box>

      {/* Gantt bar */}
      <Box
        sx={{
          display: 'flex',
          width: '100%',
          height: 18,
          border: '1px solid',
          borderColor: 'divider',
          borderRadius: '3px',
          overflow: 'hidden',
          backgroundColor: '#f5f5f5',
        }}
      >
        {Array.from({ length: TOTAL_HOURS }, (_, h) => {
          const isActive = hourSet.has(h)
          const isDayBoundary = h > 0 && h % 24 === 0
          const isTick = h > 0 && h % 6 === 0 && !isDayBoundary
          return (
            <Box
              key={h}
              sx={{
                flex: 1,
                minWidth: 0,
                backgroundColor: isActive
                  ? 'rgba(237, 108, 2, 0.30)'
                  : 'transparent',
                borderLeft: isDayBoundary
                  ? '1px solid rgba(0,0,0,0.25)'
                  : isTick
                    ? '1px solid rgba(0,0,0,0.08)'
                    : 'none',
              }}
            />
          )
        })}
      </Box>

      {/* Period labels */}
      <Box sx={{ display: 'flex', gap: 1, mt: '3px', flexWrap: 'wrap' }}>
        {windows.map((w, i) => (
          <Typography
            key={i}
            variant="caption"
            sx={{
              fontSize: '0.65rem',
              color: 'text.secondary',
              fontFamily: 'monospace',
            }}
          >
            ({w.start},{w.end})
          </Typography>
        ))}
      </Box>
    </Box>
  )
})

PeriodGantt.displayName = 'PeriodGantt'

// --- InfoRow helper ---

const InfoRow = ({
  label,
  value,
}: {
  label: string
  value: string | number
}) => (
  <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
    <Typography
      variant="body2"
      sx={{ fontSize: '0.75rem', color: 'text.secondary', fontWeight: 500 }}
    >
      {label}:
    </Typography>
    <Typography variant="body2" sx={{ fontSize: '0.75rem', fontWeight: 600 }}>
      {value}
    </Typography>
  </Box>
)

// --- SyncGroupCard ---

type SyncGroupCardProps = {
  group: SynchronizedLinesRowType
  onUpdate: (updated: SynchronizedLinesRowType) => void
}

const SyncGroupCard = memo(({ group, onUpdate }: SyncGroupCardProps) => {
  const [expanded, setExpanded] = useState(false)
  const [editState, setEditState] = useState<SynchronizedLinesRowType>(group)

  const isTemperature = group.productMasterType === 'Temperature'
  const isActive = group.productMasterType === 'Active'

  const lineNames = useMemo(() => {
    if (!group.lines || group.lines.trim() === '') return []
    return group.lines
      .split(',')
      .map((l) => l.trim())
      .filter(Boolean)
  }, [group.lines])

  const accentColor = isTemperature ? '#1976d2' : '#ed6c02'

  const handleToggle = useCallback(() => {
    setExpanded((prev) => !prev)
    // Reset edit state when opening
    if (!expanded) setEditState(group)
  }, [expanded, group])

  const handleSave = useCallback(() => {
    onUpdate(editState)
    setExpanded(false)
  }, [editState, onUpdate])

  return (
    <Paper
      variant="outlined"
      sx={{
        borderLeft: `4px solid ${accentColor}`,
        borderRadius: '6px',
        overflow: 'hidden',
      }}
    >
      <Box sx={{ p: 1.5, pb: isActive && !expanded ? 0.5 : 1.5 }}>
        {/* Header row */}
        <Box
          sx={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
            mb: 1,
          }}
        >
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            {isActive ? (
              <PeopleIcon sx={{ fontSize: 18, color: accentColor }} />
            ) : (
              <SyncIcon sx={{ fontSize: 18, color: accentColor }} />
            )}
            <Typography
              variant="subtitle2"
              sx={{ fontWeight: 700, fontSize: '0.8rem' }}
            >
              {group.groupName}
            </Typography>
            <Chip
              label={group.productMasterType}
              size="small"
              sx={{
                height: 20,
                fontSize: '0.65rem',
                fontWeight: 600,
                backgroundColor: isTemperature
                  ? 'rgba(25, 118, 210, 0.1)'
                  : 'rgba(237, 108, 2, 0.1)',
                color: accentColor,
                border: `1px solid ${accentColor}30`,
              }}
            />
          </Box>
          <IconButton size="small" onClick={handleToggle} sx={{ p: 0.25 }}>
            {expanded ? (
              <ExpandLessIcon sx={{ fontSize: 18 }} />
            ) : (
              <ExpandMoreIcon sx={{ fontSize: 18 }} />
            )}
          </IconButton>
        </Box>

        {/* Lines row */}
        {lineNames.length > 0 && (
          <Box sx={{ display: 'flex', gap: 0.5, flexWrap: 'wrap', mb: 1 }}>
            {lineNames.map((line) => (
              <Chip
                key={line}
                label={line}
                size="small"
                variant="outlined"
                sx={{
                  height: 20,
                  fontSize: '0.65rem',
                  borderColor: 'divider',
                }}
              />
            ))}
          </Box>
        )}

        {/* Type-specific info */}
        {isTemperature && (
          <InfoRow label="Changeover" value={`${group.changeoverTime}h`} />
        )}

        {isActive && (
          <Box sx={{ mb: 1 }}>
            <Box
              sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 0.75 }}
            >
              <Box
                sx={{
                  display: 'inline-flex',
                  alignItems: 'center',
                  gap: 0.5,
                  px: 1,
                  py: 0.25,
                  borderRadius: '4px',
                  backgroundColor: 'rgba(237, 108, 2, 0.08)',
                }}
              >
                <PeopleIcon sx={{ fontSize: 14, color: '#ed6c02' }} />
                <Typography
                  variant="body2"
                  sx={{
                    fontSize: '0.75rem',
                    fontWeight: 700,
                    color: '#ed6c02',
                  }}
                >
                  Max Active Lines: {group.active}
                </Typography>
              </Box>
            </Box>
            <PeriodGantt periods={group.periods} />
          </Box>
        )}
      </Box>

      {/* Expandable edit section */}
      <Collapse in={expanded}>
        <Divider />
        <Box
          sx={{
            p: 1.5,
            display: 'flex',
            flexDirection: 'column',
            gap: 1.5,
            backgroundColor: '#fafafa',
          }}
        >
          <TextField
            label="Group Name"
            size="small"
            value={editState.groupName}
            onChange={(e) =>
              setEditState({ ...editState, groupName: e.target.value })
            }
            InputProps={{ sx: { fontSize: '0.75rem' } }}
            InputLabelProps={{ sx: { fontSize: '0.75rem' } }}
          />
          <TextField
            label="Lines (comma-separated)"
            size="small"
            value={editState.lines}
            onChange={(e) =>
              setEditState({ ...editState, lines: e.target.value })
            }
            InputProps={{ sx: { fontSize: '0.75rem' } }}
            InputLabelProps={{ sx: { fontSize: '0.75rem' } }}
          />
          {isTemperature && (
            <TextField
              label="Changeover Time (hours)"
              size="small"
              type="number"
              value={editState.changeoverTime}
              onChange={(e) =>
                setEditState({
                  ...editState,
                  changeoverTime: Number(e.target.value),
                })
              }
              InputProps={{ sx: { fontSize: '0.75rem' } }}
              InputLabelProps={{ sx: { fontSize: '0.75rem' } }}
            />
          )}
          {isActive && (
            <>
              <TextField
                label="Max Active Lines"
                size="small"
                type="number"
                value={editState.active}
                onChange={(e) =>
                  setEditState({ ...editState, active: Number(e.target.value) })
                }
                InputProps={{ sx: { fontSize: '0.75rem' } }}
                InputLabelProps={{ sx: { fontSize: '0.75rem' } }}
              />
              <TextField
                label="Periods (e.g. (0,12),(48,60))"
                size="small"
                value={editState.periods ?? ''}
                onChange={(e) =>
                  setEditState({ ...editState, periods: e.target.value })
                }
                InputProps={{
                  sx: { fontSize: '0.75rem', fontFamily: 'monospace' },
                }}
                InputLabelProps={{ sx: { fontSize: '0.75rem' } }}
              />
            </>
          )}
          <Box sx={{ display: 'flex', justifyContent: 'flex-end', gap: 1 }}>
            <Button
              size="small"
              onClick={handleToggle}
              sx={{ fontSize: '0.7rem', textTransform: 'none' }}
            >
              Cancel
            </Button>
            <Button
              size="small"
              variant="contained"
              onClick={handleSave}
              sx={{ fontSize: '0.7rem', textTransform: 'none' }}
            >
              Save
            </Button>
          </Box>
        </Box>
      </Collapse>
    </Paper>
  )
})

SyncGroupCard.displayName = 'SyncGroupCard'

// --- Main component ---

type ScheduleCrewSyncProps = {
  schedulerId: string
  iteration: string
}

export const ScheduleCrewSyncView = ({
  schedulerId,
  iteration,
}: ScheduleCrewSyncProps) => {
  const API = 'synchronized-lines'

  const { data: responseData = { data: [], predicate: undefined }, isLoading } =
    useScheduleProductionRequirements(schedulerId, API, iteration)

  const { data: serverData = [] } = responseData as {
    data: SynchronizedLinesRowType[]
    predicate: string[] | undefined
  }

  const [localNewRows, setLocalNewRows] = useState<SynchronizedLinesRowType[]>(
    []
  )
  const [nextNewRowId, setNextNewRowId] = useState(-1)

  const groups = useMemo(
    () => [...serverData, ...localNewRows],
    [serverData, localNewRows]
  )

  const onUpdateSuccess = useCallback(() => {
    setLocalNewRows([])
    toast.success('Sync group updated successfully.')
  }, [])

  const onUpdateFailure = useCallback(() => {
    toast.error('Failed to update sync group.')
  }, [])

  const { mutate: saveData, isLoading: isSaving } =
    useUpdateProductionRequirements(onUpdateSuccess, onUpdateFailure)

  const handleAddGroup = useCallback(() => {
    const newRow: SynchronizedLinesRowType = {
      id: nextNewRowId,
      groupName: `Group ${Math.abs(nextNewRowId)}`,
      lines: '',
      changeoverTime: 0,
      productMasterType: 'Active',
      active: 1,
      periods: '',
    }
    setLocalNewRows((prev) => [...prev, newRow])
    setNextNewRowId((prev) => prev - 1)
  }, [nextNewRowId])

  const handleUpdate = useCallback(
    (updated: SynchronizedLinesRowType) => {
      const changedRows: Record<number, SynchronizedLinesRowType> = {
        [updated.id]: updated,
      }
      saveData({ data: changedRows, schedulerId, API, iteration })
    },
    [saveData, schedulerId, iteration]
  )

  if (isLoading) {
    return (
      <Box
        sx={{
          display: 'flex',
          justifyContent: 'center',
          alignItems: 'center',
          py: 6,
        }}
      >
        <CircularProgress size={28} />
      </Box>
    )
  }

  return (
    <Box sx={{ width: '100%' }}>
      {/* Header */}
      <Box
        sx={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          mb: 2,
        }}
      >
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          <SyncIcon sx={{ fontSize: 20, color: 'text.secondary' }} />
          <Typography
            variant="subtitle1"
            sx={{ fontWeight: 700, fontSize: '0.85rem' }}
          >
            Crew & Synchronization Groups
          </Typography>
          {isSaving && <CircularProgress size={14} sx={{ ml: 1 }} />}
        </Box>
        <Button
          variant="outlined"
          size="small"
          startIcon={<AddIcon sx={{ fontSize: 16 }} />}
          onClick={handleAddGroup}
          sx={{ fontSize: '0.7rem', textTransform: 'none' }}
        >
          Add Group
        </Button>
      </Box>

      {/* Group cards */}
      {groups.length === 0 ? (
        <Paper
          variant="outlined"
          sx={{
            p: 4,
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'center',
            gap: 1,
            borderStyle: 'dashed',
            borderRadius: '6px',
          }}
        >
          <SyncIcon sx={{ fontSize: 32, color: 'text.disabled' }} />
          <Typography
            variant="body2"
            sx={{ fontSize: '0.8rem', color: 'text.secondary' }}
          >
            No synchronization groups configured
          </Typography>
          <Button
            size="small"
            startIcon={<AddIcon sx={{ fontSize: 14 }} />}
            onClick={handleAddGroup}
            sx={{ fontSize: '0.7rem', textTransform: 'none', mt: 0.5 }}
          >
            Add your first group
          </Button>
        </Paper>
      ) : (
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.5 }}>
          {groups.map((group) => (
            <SyncGroupCard
              key={group.id}
              group={group}
              onUpdate={handleUpdate}
            />
          ))}
        </Box>
      )}
    </Box>
  )
}
